import { isAllHtmlElements } from "./is-all-html-elements";

/**
 * Synchronize heights of elements
 * @param {Array<HTMLElement|null>} elements - Elements to synchronize
 * @param {'contentLength' | 'currentHeight'} [options.source='contentLength'] -
 * Method used to get the height value to sync across elements. `contentLength`
 * (innerText.length) is reliable for pure text content when responding to
 * window resizing. Otherwise, currentHeight can  be used to sync the tallest
 * element's height to all the other elements. Note that this works poorly
 * unless content is allowed to reflow with explicit height attributes removed
 * between synchronization calls.
 */
export const syncElementHeights = (
  elements: Array<HTMLElement | null> | NodeList,
  {
    source = "contentLength",
  }: {
    source?: "contentLength" | "currentHeight";
  } = {}
) => {
  const els = elements instanceof NodeList ? Array.from(elements) : elements;

  if (els.length === 0 || !isAllHtmlElements(els)) {
    return;
  }

  const heightSource =
    source === "contentLength"
      ? els.sort((a, b) => {
          return b.innerText.length - a.innerText.length;
        })[0]
      : els.sort((a, b) => {
          return b.clientHeight - a.clientHeight;
        })[0];

  const syncedHeight = heightSource?.clientHeight || 0;

  for (const el of els) {
    if (el.clientHeight !== syncedHeight) {
      el.style["height"] = syncedHeight + "px";
    }
  }
};
